import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { FEATURE_FLAGS } from 'constants/security'

import auth from 'utils/auth'

import { withAppService } from 'services/app'
import appSelectors from 'services/app/selectors'
import { withChildBookingService, withChildBookingServiceProps } from 'services/booking/childBooking'
import { withChildService, withChildServiceProps } from 'services/legacy/child'
import { withChildSessionsService, withChildSessionsServiceProps } from 'services/legacy/childSessions'
import * as childSessionsSelectors from 'services/legacy/childSessions/selectors'
import nurseryContextSelectors from 'services/nurseryContext/selectors'
import { withRouter, withRouterProps } from 'services/router'
import { withSecurityService, withSecurityServiceProps } from 'services/security'

import { RootState } from 'core/reducers'

import ChildAdd from '../../ChildAdd'
import ChildProfileHistory from '../ChildProfileHistory'
import ChildProfileView from './ChildProfileView'

const CHILD_GROUPS = {
  read: [
    'child.keyWorkers',
    'user',
    'user.details',
    'child.carerChildRelations',
    'carerChildRelation.carer',
    'carer',
    'child.extend',
    'child.nurseryClass',
    'child.ethnicity',
    'nurseryClass',
    'childProfile',
    'childInformation',
    'ethnicity',
    'user.details',
    'child.registrationForms',
    'registrationForm',
    'registrationForm.carer',
  ],
}

type ChildProfileContainerProps = withChildServiceProps
  & withChildBookingServiceProps
  & withChildSessionsServiceProps
  & withSecurityServiceProps
  & withRouterProps

const mapState = (state: RootState, {
  childSelectors,
  childSingleState,
  securitySelectors,
}: ChildProfileContainerProps) => {
  const isRegistrationFormEnabled = auth.SELECTORS.getIsAuthorised(state, {
    flags: [FEATURE_FLAGS.REGISTRATION_FORMS],
  })

  return {
    child: childSelectors.getChildSelector(state),
    drinkingMethods: appSelectors.getDrinkingMethods(),
    isFetching: appSelectors.getIsFetching(childSingleState),
    isFinanceV3Enabled: auth.SELECTORS.getIsFinanceV3Enabled(state),
    permissionToEdit:
      securitySelectors.isAdministrationContext(state)
      || auth.SELECTORS.getComponentIsAuthorised(state, ChildAdd),
    permissionToProfileHistory: auth.SELECTORS.getComponentIsAuthorised(state, ChildProfileHistory),
    registrationForm: {
      isRegistrationFormEnabled,
      ...(isRegistrationFormEnabled && { ...nurseryContextSelectors.getNurseryFormBuilderSettings(state) }),
    },
    toiletTrainingOptions: appSelectors.getToiletTrainingOptions(),
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const ChildProfileContainer: React.FC<ChildProfileContainerProps & PropsFromRedux> = ({
  child,
  childActions,
  childBookingActions,
  childBookingSelectors,
  childSessionsActions,
  drinkingMethods,
  isFetching,
  isFinanceV3Enabled,
  params,
  permissionToEdit,
  permissionToProfileHistory,
  registrationForm,
  toiletTrainingOptions,
}) => {
  const [isFetchingBookingPattern, setIsFetchingBookingPattern] = useState<boolean>(true)
  const [hasBookingPattern, setHasBookingPattern] = useState<boolean>(false)

  const fetchData = () => {
    setIsFetchingBookingPattern(true)

    childActions.get({
      params: [params?.childId, {
        groups: CHILD_GROUPS,
      }],
    })

    if (isFinanceV3Enabled) {
      const criteria = childBookingSelectors.getCriteria({
        childId: child?.id as number,
      })

      const childBookingApiParams = {
        criteria,
        limit: 0,
        page: 1,
      }

      const deviceTokenNotRequired = false

      childBookingActions.list({
        onFailed: () => { setIsFetchingBookingPattern(false) },
        onSuccess: ({ meta }) => {
          setHasBookingPattern(1 <= meta?.total_results)
          setIsFetchingBookingPattern(false)
        },
        onlyData: true,
        params: [deviceTokenNotRequired, childBookingApiParams],
      })
    } else {
      const childSessionsApiParams = {
        criteria: childSessionsSelectors.getCriteria({ archived: 0, sessionDate: new Date() }),
        limit: 1,
        order: 'startDate DESC',
      }

      childSessionsActions.groupedByList(child?.id, childSessionsApiParams, ({ data }) => {
        setHasBookingPattern(!!data?.current?.length)
        setIsFetchingBookingPattern(false)
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  return (
    <ChildProfileView
      child={child}
      drinkingMethods={drinkingMethods}
      hasBookingPattern={hasBookingPattern}
      isLoading={isFetching || isFetchingBookingPattern}
      permissionToEdit={permissionToEdit}
      permissionToProfileHistory={permissionToProfileHistory}
      registrationForm={registrationForm}
      toiletTrainingOptions={toiletTrainingOptions}
      onRegistrationFormSendSubmitSuccess={fetchData}
    />
  )
}

const enhance = compose(
  withAppService,
  withChildService,
  withChildSessionsService,
  withChildBookingService,
  withSecurityService,
  withRouter,
  connector,
)

export default enhance(ChildProfileContainer)
